import { useMemo } from 'react';
import { useStore } from '@nanostores/react';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AccountsEmailSetupStatusResponse,
  DataOutIntegration,
  IntegrationsGetSegmentOutLegacyResponse,
  MParticleIntegration,
  SQLImportRow,
} from '@customerio/hydra-ui-api';
import { accountStore } from '@/services/auth/store';
import { VerificationState } from '@/services/auth/types';
import { getActionCredentialQueryOptions, type ActionCredentialResult } from '@/services/db/action-credential/queries';
import type { ApiCredential } from '@/services/db/api-credential/entity';
import { getApiCredentialsForEnvironmentQueryOptions } from '@/services/db/api-credential/queries';
import type { AudienceCredential } from '@/services/db/audience-credential/entity';
import { getAudienceCredentialsQueryOptions } from '@/services/db/audience-credential/queries';
import type { Audience } from '@/services/db/audience/entity';
import { getAudiencesQueryOptions } from '@/services/db/audience/queries';
import { isApiForm, isJotform, isScannedForm, isTypeform, type CustomForm } from '@/services/db/custom-form/entity';
import { getCustomFormsQueryOptions } from '@/services/db/custom-form/queries';
import { getEmailSetupStatusQueryOptions } from '@/services/db/email-setup-status/queries';
import { useEnvironmentOptional } from '@/services/db/environment/queries';
import {
  isConnectedFacebookForm,
  type FacebookLeadAdsCredential,
  type FacebookLeadAdsForm,
} from '@/services/db/facebook-lead-ads/entity';
import {
  getFacebookLeadAdsCredentialQueryOptions,
  getFacebookLeadAdsFormsQueryOptions,
} from '@/services/db/facebook-lead-ads/queries';
import { getIntegrationsQueryOptions } from '@/services/db/integration/queries';
import {
  getDataOutQueryOptions,
  getMparticleIntegrationQueryOptions,
  getSegmentOutQueryOptions,
} from '@/services/db/integration/status-queries';
import { sqlImportRowIsFailed, sqlImportRowIsPartiallyFailed, type SqlImport } from '@/services/db/sql-import/entity';
import { getSqlImportRowsQueryOptions, useSqlImportsByDatabase } from '@/services/db/sql-import/queries';
import { warehouseSyncIsPaused, type WarehouseSync } from '@/services/db/warehouse-sync/entity';
import { getWarehouseSyncQueryOptions } from '@/services/db/warehouse-sync/queries';
import type { Webhook } from '@/services/db/webhook/entity';
import { getWebhooksQueryOptions } from '@/services/db/webhook/queries';
import { isEmptyString } from '@/utils/string';
import { IntegrationType } from '../catalog/enums';
import { useIntegrationsWorkspaceId } from '../workspace-id';
import type {
  AdAudienceSource,
  ConnectionSources,
  FormSource,
  ReportingWebhookSource,
  SqlImportRowSource,
  SqlImportSyncSource,
} from './view-model/types';

/** A `data`-only view of one `useQueries` result (module builders read only `.data`). */
type QueryData<T> = { readonly data?: T };

/** Ad-network display names keyed by network slug. */
const AD_NETWORK_NAMES: Record<string, string> = {
  adwords: 'Google Ads',
  facebook: 'Facebook Ads',
};

/** Push provider key → the device platform key(s) it configures. */
const PUSH_PROVIDER_PLATFORMS: Record<string, readonly string[]> = {
  fcm: ['android'],
  fcm_ios: ['android', 'ios'],
  apns: ['ios'],
  apns_sandbox: ['ios'],
};

const dateToEpochSeconds = (date: Date): number => Math.floor(date.getTime() / 1000);

/** Join words, using a distinct separator between the final two. */
function joinWords(words: readonly string[], sep = ', ', lastTwoSep = ' and '): string {
  if (words.length <= 2) {
    return words.join(lastTwoSep);
  }
  const allButLastTwo = words.slice(0, words.length - 2);
  const lastTwo = words.slice(words.length - 2);
  return [...allButLastTwo, lastTwo.join(`,${lastTwoSep}`)].join(sep);
}

/** The environment's enabled identifiers, joined for the form error message. */
function availableIdentifiersLabel(identifiers: Record<string, number> | undefined): string {
  if (identifiers == null) {
    return '';
  }
  const enabled = Object.keys(identifiers).filter((key) => key !== 'cio_id' && (identifiers[key] ?? 0) !== 0);
  return joinWords(enabled, ' ', ' or ');
}

/** Human-readable message for a custom-form identifier error key. */
function formErrorMessage(errorKey: string, availableIdentifiers: string): string | undefined {
  switch (errorKey) {
    case 'missing_identifier':
      return `A valid ${availableIdentifiers} was not found`;
    case 'multiple_identifiers':
      return 'Multiple identifiers were found';
    case 'invalid_email_identifier':
      return 'Invalid email identifier was found';
    default:
      return undefined;
  }
}

function toFormSource(form: CustomForm, availableIdentifiers: string): FormSource {
  const hasError = !isEmptyString(form.error);
  return {
    id: form.id,
    name: form.name,
    formType: form.form_type,
    formId: form.form_id,
    createdAt: form.created_at,
    lastActivityAt: form.last_activity_at,
    status: form.status,
    hasError,
    errorMessage: hasError ? formErrorMessage(form.error, availableIdentifiers) : undefined,
    isScannedForm: isScannedForm(form),
    isApiForm: isApiForm(form),
    isTypeform: isTypeform(form),
    isJotform: isJotform(form),
  };
}

function mapForms(forms: readonly CustomForm[] | undefined, availableIdentifiers: string): readonly FormSource[] {
  return (forms ?? []).map((form) => toFormSource(form, availableIdentifiers));
}

function toSqlImportRowSource(row: SQLImportRow): SqlImportRowSource {
  return {
    createdAt: row.created_at,
    endedAt: row.ended_at,
    isFailed: sqlImportRowIsFailed(row),
    isPartiallyFailed: sqlImportRowIsPartiallyFailed(row),
    error: row.error,
  };
}

function mapApiCredentials(credentials: readonly ApiCredential[] | undefined): ConnectionSources['apiCredentials'] {
  return (credentials ?? []).map((credential) => ({
    lastUsedAt: dateToEpochSeconds(credential.last_used_at),
    createdAt: dateToEpochSeconds(credential.created_at),
  }));
}

function buildWarehouseSync(
  sync: WarehouseSync | null | undefined,
  twilioEnabled: boolean,
): ConnectionSources['warehouseSync'] {
  if (sync == null) {
    return null;
  }
  return {
    createdAt: sync.created_at ?? 0,
    // `export_files` is `[]*WarehouseExportFile` with no `omitempty` in the handler
    // (services ui_api/model/warehouse_sync.go:57), so a nil slice reaches us as JSON
    // `null` even though the generated schema types it as a required array.
    lastActivity: Math.max(0, ...(sync.export_files ?? []).map((file) => file.created_at)),
    isPaused: warehouseSyncIsPaused(sync, twilioEnabled),
  };
}

/** Collect the fetched rows keyed by their sql-import id (index-aligned with `ids`). */
function collectRowsBySyncId(
  ids: readonly string[],
  queries: readonly QueryData<readonly SQLImportRow[]>[],
): Map<string, readonly SQLImportRow[]> {
  const rows = new Map<string, readonly SQLImportRow[]>();
  ids.forEach((id, index) => {
    rows.set(id, queries[index]?.data ?? []);
  });
  return rows;
}

function resolveSyncImports(
  sync: SqlImport,
  rowsBySyncId: ReadonlyMap<string, readonly SQLImportRow[]>,
): readonly SqlImportRowSource[] {
  if (!sync.active) {
    return [];
  }
  return (rowsBySyncId.get(sync.id) ?? []).map(toSqlImportRowSource);
}

function toSqlImportSyncSource(
  sync: SqlImport,
  rowsBySyncId: ReadonlyMap<string, readonly SQLImportRow[]>,
): SqlImportSyncSource {
  return {
    active: sync.active,
    // Deliberately not `?? 0`: zero is a real epoch value and would win the group minimum,
    // erasing the Date added for every other sync on the database.
    createdAt: sync.created_at,
    lastSyncedAt: sync.last_synced_at ?? 0,
    imports: resolveSyncImports(sync, rowsBySyncId),
  };
}

function buildSqlImportsByDatabase(
  syncsByDatabase: Readonly<Record<string, readonly SqlImport[]>>,
  rowsBySyncId: ReadonlyMap<string, readonly SQLImportRow[]>,
): Record<string, readonly SqlImportSyncSource[]> {
  const record: Record<string, readonly SqlImportSyncSource[]> = {};
  for (const [database, syncs] of Object.entries(syncsByDatabase)) {
    record[database] = syncs.map((sync) => toSqlImportSyncSource(sync, rowsBySyncId));
  }
  return record;
}

/** Collect the enabled NativeDataOut configs keyed by integration id. */
function collectDataOut(
  ids: readonly string[],
  queries: readonly QueryData<DataOutIntegration | null>[],
): ConnectionSources['dataOut'] {
  const dataOut: ConnectionSources['dataOut'] = {};
  ids.forEach((id, index) => {
    const data = queries[index]?.data;
    if (data != null) {
      dataOut[id] = {
        createdAt: data.created_at,
        lastActivityAt: data.last_activity_at,
        enabled: data.enabled,
        events: data.events ?? null,
      };
    }
  });
  return dataOut;
}

function audienceCredentialExpired(audience: Audience, credentials: readonly AudienceCredential[]): boolean {
  const credential = credentials.find((c) => c.type === audience.type);
  return credential?.expired === true;
}

function audienceLastSynced(audience: Audience): number {
  // `meta` is `map[string]any` with no `omitempty` (services ui_api/model/audience.go:9), so a
  // nil map arrives as JSON `null` — indexing it directly would throw, and `?? 0` on the
  // property does not help because the throw happens before it.
  const meta =
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    (audience.meta ?? {}) as Record<string, unknown>;
  return Number(meta['sync_time'] ?? 0);
}

function toAdAudienceSource(audience: Audience, credentials: readonly AudienceCredential[]): AdAudienceSource {
  return {
    id: audience.id,
    name: audience.name,
    state: audience.state,
    adNetworkName: AD_NETWORK_NAMES[audience.type] ?? '',
    credentialExpired: audienceCredentialExpired(audience, credentials),
    createdAt: dateToEpochSeconds(audience.created_at),
    lastSynced: audienceLastSynced(audience),
    lastError: audience.last_error ?? '',
  };
}

function buildAdAudiences(
  audiences: readonly Audience[] | undefined,
  credentials: readonly AudienceCredential[] | undefined,
): ConnectionSources['adAudiences'] {
  if (audiences == null) {
    return null;
  }
  const creds = credentials ?? [];
  const result: NonNullable<ConnectionSources['adAudiences']> = {};
  for (const network of ['adwords', 'facebook']) {
    result[network] = audiences
      .filter((audience) => audience.type === network)
      .map((audience) => toAdAudienceSource(audience, creds));
  }
  return result;
}

function mapReportingWebhooks(webhooks: readonly Webhook[] | undefined): readonly ReportingWebhookSource[] {
  return (webhooks ?? []).map((webhook) => ({
    id: webhook.id,
    name: webhook.name,
    events: webhook.events ?? null,
    createdAt: dateToEpochSeconds(webhook.created_at),
    lastActivityAt: dateToEpochSeconds(webhook.last_activity_at),
    enabled: webhook.enabled,
  }));
}

function mapSegmentOut(
  segmentOut: IntegrationsGetSegmentOutLegacyResponse | null | undefined,
): ConnectionSources['segmentOut'] {
  if (segmentOut == null) {
    return null;
  }
  return {
    createdAt: segmentOut.created_at,
    lastActivity: segmentOut.last_activity_at,
    enabled: segmentOut.enabled,
  };
}

function mapMparticle(mparticle: MParticleIntegration | null | undefined): ConnectionSources['mparticle'] {
  if (mparticle == null) {
    return null;
  }
  return {
    createdAt: mparticle.created_at,
    lastActivityAt: mparticle.last_activity_at,
    enabled: mparticle.enabled,
  };
}

function buildFacebookLeadAds(
  credential: FacebookLeadAdsCredential | null | undefined,
  forms: readonly FacebookLeadAdsForm[] | undefined,
): ConnectionSources['facebookLeadAds'] {
  const connectedForms = (forms ?? []).filter(isConnectedFacebookForm);
  return {
    enabled: credential != null,
    credentialCreatedAt: credential?.created_at,
    connectedFormsLastActivity: connectedForms.map((form) => form.last_activity_at),
  };
}

/** The credential's creation timestamp, or undefined when absent/unfetched. */
function credentialCreated(result: ActionCredentialResult | undefined): number | undefined {
  return result?.credential?.created;
}

/**
 * Whether the credential read *succeeded* and resolved to exactly `null` — the inverted
 * Slack/Twilio guard, which shows the row when the value is null (ported verbatim).
 *
 * `present` matters because our reads normalize failure to `{ credential: null }`, which the
 * inversion would otherwise read as connected. Ember reads `fetchSlackCredentials.last?.value`,
 * which is `undefined` on a failed task and so fails its `!== null` check
 * (integrations/all/overview/controller.ts:872-875) — a failed fetch shows no row there, and
 * must not show one here.
 */
function credentialIsNull(result: ActionCredentialResult | undefined): boolean {
  return result?.present === true && result.credential === null;
}

function buildPush(
  pushProviders: readonly string[],
  fcmCreated: number | undefined,
  apnsCreated: number | undefined,
): ConnectionSources['push'] {
  const platforms = [...new Set(pushProviders.flatMap((provider) => PUSH_PROVIDER_PLATFORMS[provider] ?? []))];
  const createdTimestamps = [fcmCreated ?? 0, apnsCreated ?? 0].filter((timestamp) => timestamp > 0);
  return {
    platforms,
    createdAt: createdTimestamps.length > 0 ? Math.min(...createdTimestamps) : undefined,
  };
}

function buildUrlParams(result: ActionCredentialResult | undefined): ConnectionSources['urlParams'] {
  return {
    enabled: result?.present === true,
    created: result?.credential?.created,
  };
}

/**
 * The Email channel gate: a verified sending domain, a From address, and an
 * approved account verification state (mirrors Ember `isEmailSetupComplete`,
 * where the two setup flags come from `email_setup_status`).
 */
function buildEmailSetupComplete(
  status: AccountsEmailSetupStatusResponse | undefined,
  verificationState: string | undefined,
): boolean {
  if (status == null) {
    return false;
  }
  const emailConfigured = status.has_verified_sending_domain && status.has_from_address;
  return emailConfigured && verificationState === VerificationState.Approved;
}

/**
 * Fan out every overview-only data source the connections parsers read and
 * assemble the {@link ConnectionSources} bag. Each source degrades gracefully:
 * a failed / not-configured fetch resolves to its "absent" default
 * (`null` / `false` / `[]` / omitted) rather than blanking the whole tab. All
 * timestamps are normalized to unix seconds. The per-source shaping lives in
 * the module-level builders above; this hook only wires queries to them.
 */
// fallow-ignore-next-line complexity -- inherent fan-out: one TanStack query per overview data source (~20 independent sources) plus environment/account context. The hook count is the source count; splitting only relocates it. Pure per-source shaping lives in the module-level builders above.
export function useConnectionSources(): { sources: ConnectionSources; isLoading: boolean } {
  const workspaceId = useIntegrationsWorkspaceId();
  const qc = useQueryClient();
  const account = useStore(accountStore);
  const accountId = account?.id != null ? Number(account.id) : undefined;

  const environment = useEnvironmentOptional(workspaceId);
  const env = environment.data?.environment;
  const enabledActions = env?.enabled_actions ?? [];
  const twilioEnabled = enabledActions.includes('twilio');
  const pushProviders = env?.push_providers ?? [];
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  const availableIdentifiers = availableIdentifiersLabel(env?.identifiers as Record<string, number> | undefined);

  const catalog = useQuery(getIntegrationsQueryOptions(workspaceId, qc));
  const warehouse = useQuery(getWarehouseSyncQueryOptions(workspaceId));
  const audiences = useQuery(getAudiencesQueryOptions(workspaceId, qc));
  const audienceCredentials = useQuery(getAudienceCredentialsQueryOptions(workspaceId, qc));
  const customForms = useQuery(getCustomFormsQueryOptions(workspaceId, qc));
  const mparticle = useQuery(getMparticleIntegrationQueryOptions(workspaceId));
  const fbCredential = useQuery(getFacebookLeadAdsCredentialQueryOptions(workspaceId));
  const fbForms = useQuery(getFacebookLeadAdsFormsQueryOptions(workspaceId, qc));
  const segmentOut = useQuery(getSegmentOutQueryOptions(workspaceId));
  const webhooks = useQuery(getWebhooksQueryOptions(workspaceId, qc));
  const apiCredentials = useQuery({
    ...getApiCredentialsForEnvironmentQueryOptions(accountId ?? 0, workspaceId),
    enabled: accountId != null,
  });
  const emailSetup = useQuery({
    ...getEmailSetupStatusQueryOptions(accountId ?? 0),
    enabled: accountId != null,
  });

  const slackCredential = useQuery(getActionCredentialQueryOptions(workspaceId, 'slack'));
  const twilioCredential = useQuery(getActionCredentialQueryOptions(workspaceId, 'twilio'));
  const inAppCredential = useQuery(getActionCredentialQueryOptions(workspaceId, 'in_app'));
  const fcmCredential = useQuery(getActionCredentialQueryOptions(workspaceId, 'push_fcm'));
  const apnsCredential = useQuery(getActionCredentialQueryOptions(workspaceId, 'push_apns'));
  const urlParamsCredential = useQuery(getActionCredentialQueryOptions(workspaceId, 'url_params'));

  // NativeDataOut (amplitude / mixpanel) — one full-object read per catalog id.
  const nativeDataOutIds = useMemo(
    () => (catalog.data ?? []).filter((i) => i.type === IntegrationType.nativeDataOut).map((i) => i.id),
    [catalog.data],
  );
  const dataOutQueries = useQueries({
    queries: nativeDataOutIds.map((id) => getDataOutQueryOptions(workspaceId, id)),
  });

  // SQL imports — the reworked six-per-database read (one query per database,
  // combined into a record keyed by database slug).
  const { byDatabase: sqlByDatabase, isPending: sqlImportsPending } = useSqlImportsByDatabase(workspaceId);
  // Rows are a separate endpoint; only active syncs need them (the parser reads
  // `sync.imports` only for active syncs).
  const activeSyncIds = useMemo(() => {
    const ids: string[] = [];
    for (const syncs of Object.values(sqlByDatabase)) {
      // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
      for (const sync of syncs as SqlImport[]) {
        if (sync.active) {
          ids.push(sync.id);
        }
      }
    }
    return ids;
  }, [sqlByDatabase]);
  const sqlRowQueries = useQueries({
    queries: activeSyncIds.map((id) => getSqlImportRowsQueryOptions(workspaceId, id)),
  });

  const sources = useMemo<ConnectionSources>(() => {
    const dataOut = collectDataOut(nativeDataOutIds, dataOutQueries);
    const rowsBySyncId = collectRowsBySyncId(activeSyncIds, sqlRowQueries);

    return {
      enabledActions,
      apiCredentials: mapApiCredentials(apiCredentials.data),
      warehouseSync: buildWarehouseSync(warehouse.data, twilioEnabled),
      sqlImportsByDatabase: buildSqlImportsByDatabase(sqlByDatabase, rowsBySyncId),
      dataOut,
      segmentOut: mapSegmentOut(segmentOut.data),
      mparticle: mapMparticle(mparticle.data),
      adAudiences: buildAdAudiences(audiences.data, audienceCredentials.data),
      facebookLeadAds: buildFacebookLeadAds(fbCredential.data, fbForms.data),
      reportingWebhooks: mapReportingWebhooks(webhooks.data),
      forms: mapForms(customForms.data, availableIdentifiers),
      inAppCreated: credentialCreated(inAppCredential.data),
      push: buildPush(pushProviders, credentialCreated(fcmCredential.data), credentialCreated(apnsCredential.data)),
      slackCredentialIsNull: credentialIsNull(slackCredential.data),
      twilioCredentialIsNull: credentialIsNull(twilioCredential.data),
      emailSetupComplete: buildEmailSetupComplete(emailSetup.data, account?.verification_state),
      urlParams: buildUrlParams(urlParamsCredential.data),
    };
  }, [
    account?.verification_state,
    enabledActions,
    availableIdentifiers,
    twilioEnabled,
    pushProviders,
    nativeDataOutIds,
    dataOutQueries,
    activeSyncIds,
    sqlByDatabase,
    sqlRowQueries,
    warehouse.data,
    audiences.data,
    audienceCredentials.data,
    customForms.data,
    fbCredential.data,
    fbForms.data,
    mparticle.data,
    segmentOut.data,
    webhooks.data,
    apiCredentials.data,
    emailSetup.data,
    slackCredential.data,
    twilioCredential.data,
    inAppCredential.data,
    fcmCredential.data,
    apnsCredential.data,
    urlParamsCredential.data,
  ]);

  const isLoading =
    [
      environment,
      catalog,
      warehouse,
      audiences,
      audienceCredentials,
      customForms,
      fbCredential,
      fbForms,
      mparticle,
      segmentOut,
      webhooks,
      apiCredentials,
      emailSetup,
      slackCredential,
      twilioCredential,
      inAppCredential,
      fcmCredential,
      apnsCredential,
      urlParamsCredential,
    ].some((query) => query.isLoading) ||
    dataOutQueries.some((query) => query.isLoading) ||
    sqlImportsPending ||
    sqlRowQueries.some((query) => query.isLoading);

  return { sources, isLoading };
}
